import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CancelPlanDialog } from "@/components/customer/CancelPlanDialog";
import { PlanCheckoutDialog } from "@/components/customer/PlanCheckoutDialog";
import { fmtMoney } from "@/lib/format";
import { apiError } from "@/lib/api";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles, Crown, BadgeCheck, CalendarClock, Star, Landmark, Hourglass } from "lucide-react";
import { toast } from "sonner";

type Interval = "month" | "year";

export default function Planes() {
  const { user, refresh } = useAuth();
  const [data, setData] = useState<any>(null);
  const [interval, setInterval] = useState<Interval>("month");
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const load = () => api.get("/payments/plans").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  // ?cancelar=1 (desde el dashboard) abre directamente el diálogo de cancelación.
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState(params.get("cancelar") === "1");
  const [undoing, setUndoing] = useState(false);
  const [checkout, setCheckout] = useState<{ lookupKey: string; label: string } | null>(null);
  const closeCancel = (o: boolean) => { setCancelOpen(o); if (!o && params.get("cancelar")) setParams({}, { replace: true }); };

  const keepPlan = async () => {
    setUndoing(true);
    try {
      await api.post("/payments/cancel/undo");
      toast.success("¡Qué bueno! Tu plan sigue activo y se renovará como siempre.");
      await load();
      refresh();
    } catch (e: any) {
      toast.error(apiError(e));
    } finally {
      setUndoing(false);
    }
  };

  const current = data?.current;
  const hasActive = current?.subscription_status === "active";
  const isPastDue = current?.subscription_status === "past_due";
  const cancelScheduled = hasActive && !!current?.cancel_at;
  const debit = data?.debit;
  const isDebit = current?.billing_channel === "debito";
  const pendingRequest = debit?.pending_request;
  const outstanding: any[] = debit?.outstanding || [];
  const per = (iv?: string) => (iv === "year" ? "/año" : "/mes");

  // Con un plan activo se cambia sobre la misma suscripción (prorrateo en la
  // próxima factura); nunca se abre un segundo checkout.
  const changePlan = async (lookup_key: string, label: string, newInterval: Interval) => {
    // Pasar entre mensual y anual reinicia el ciclo y Stripe cobra en el acto.
    const charge =
      newInterval !== current?.plan_interval
        ? "Al cambiar entre mensual y anual se cobra hoy el nuevo periodo, descontando lo no usado de tu plan actual."
        : "La diferencia se prorrateará y se verá reflejada en tu próxima factura.";
    const ok = window.confirm(`Cambiarás tu plan a ${label}. ${charge} ¿Deseas continuar?`);
    if (!ok) return;
    setLoadingKey(lookup_key);
    try {
      await api.post("/payments/change-plan", { lookup_key });
      toast.success("Tu plan fue actualizado");
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "No se pudo cambiar el plan");
    } finally {
      setLoadingKey(null);
    }
  };


  if (!data)
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-7 h-7 text-gold animate-spin" />
      </div>
    );

  const fmtDate = (iso?: string) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("es-EC", { day: "2-digit", month: "long", year: "numeric" }); }
    catch { return ""; }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="customer-plans">
      <div>
        <p className="text-sm text-slate-500">Planes Armony Contigo</p>
        <h1 className="text-3xl font-serif font-medium">Mis planes</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          Ya disfrutas de Armony Contigo, sin costo. Da el siguiente paso con Armony Contigo Plus o Premium:
          contenido de salud mental, autogestión y atención profesional.
        </p>
      </div>

      {data.stripe_simulated && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-sm text-amber-900" data-testid="stripe-simulated-banner" role="status">
          <strong>Entorno de pruebas:</strong> los pagos con tarjeta son simulados. No se cobra nada ni se pide una tarjeta.
        </div>
      )}

      {hasActive && (
        <div className="card-obsidian p-5 flex flex-wrap items-center gap-4 border-l-4 border-gold" data-testid="current-plan-banner">
          <div className="w-11 h-11 rounded-full bg-gold/15 flex items-center justify-center">
            <BadgeCheck className="w-6 h-6 text-gold" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm text-slate-500">Tu plan activo</p>
            <p className="text-lg font-serif font-medium">
              {current.plan_name} · {current.plan_interval === "year" ? "Anual" : "Mensual"}
            </p>
            {isDebit && (
              <p className="text-xs text-slate-600 flex items-center gap-1 mt-0.5" data-testid="current-plan-debit">
                <Landmark className="w-3.5 h-3.5" aria-hidden="true" /> Débito automático Armony · {fmtMoney(current.price)}{per(current.plan_interval)}
              </p>
            )}
          </div>
          {cancelScheduled ? (
            <div className="flex items-center gap-2 text-sm text-amber-800" data-testid="current-plan-cancel-at">
              <CalendarClock className="w-4 h-4" aria-hidden="true" />
              Termina el {fmtDate(current.cancel_at)} · no se renovará
            </div>
          ) : current.current_period_end && (
            <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="current-plan-renewal">
              <CalendarClock className="w-4 h-4 text-slate-400" aria-hidden="true" />
              Renueva el {fmtDate(current.current_period_end)}
            </div>
          )}
          {cancelScheduled ? (
            <Button data-testid="keep-plan-button" onClick={keepPlan} disabled={undoing} className="gold-gradient-bg text-white font-semibold rounded-full">
              {undoing ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Procesando" /> : "Mantener mi plan"}
            </Button>
          ) : (
            <Button data-testid="cancel-plan-button" variant="outline" onClick={() => setCancelOpen(true)} className="rounded-full border-slate-300 text-slate-700 hover:text-red-700 hover:border-red-300">
              Cancelar plan
            </Button>
          )}
        </div>
      )}

      {cancelScheduled && (
        <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/40 text-sm text-amber-900" data-testid="cancel-scheduled-banner" role="status">
          Cancelaste tu plan {current.plan_name}. Seguirás teniendo acceso hasta el <strong>{fmtDate(current.cancel_at)}</strong> y no se te volverá a cobrar.
          Después sigues en Armony Contigo, sin costo, con tus puntos y tu nivel. Si cambias de opinión, elige “Mantener mi plan”.
        </div>
      )}

      {pendingRequest && (
        <div className="p-4 rounded-xl bg-navy/5 border border-navy/20 text-sm text-slate-800 flex items-start gap-2" data-testid="debit-pending-request" role="status">
          <Hourglass className="w-4 h-4 mt-0.5 shrink-0 text-navy" aria-hidden="true" />
          <span>Pediste <strong>{pendingRequest.plan_name}</strong> con tu débito automático de Armony ({fmtMoney(pendingRequest.amount)}{per(pendingRequest.plan_interval)}).
            {pendingRequest.status === "fallido" ? " Armony no pudo realizar el primer cobro; comunícate con Armony para regularizarlo." : " Se activará en cuanto Armony confirme el cobro."}</span>
        </div>
      )}

      {hasActive && current.next_plan && !cancelScheduled && (
        <div className="p-4 rounded-xl bg-navy/5 border border-navy/20 text-sm text-slate-800 flex flex-wrap items-center gap-3" data-testid="next-plan-banner" role="status">
          <CalendarClock className="w-4 h-4 shrink-0 text-navy" aria-hidden="true" />
          <span className="flex-1 min-w-[200px]">Desde el <strong>{fmtDate(current.current_period_end)}</strong> pasas a <strong>{current.next_plan_name} · {current.next_interval === "year" ? "Anual" : "Mensual"}</strong> por {fmtMoney(current.next_price)}{per(current.next_interval)}.</span>
          {current.plan_interval && (
            <Button size="sm" variant="outline" className="rounded-full border-slate-300 text-slate-700" data-testid="keep-current-plan"
              onClick={() => { const p = data.plans.find((x: any) => x.id === current.plan); if (p) setCheckout({ lookupKey: p.prices[current.plan_interval].lookup_key, label: `${p.name} · ${current.plan_interval === "year" ? "Anual" : "Mensual"}` }); }}>
              Mantener mi plan actual
            </Button>
          )}
        </div>
      )}

      {outstanding.length > 0 && (
        <div className="card-obsidian p-5" data-testid="debit-outstanding">
          <p className="text-sm font-medium text-slate-800">Cobros en tu débito automático de Armony · {fmtMoney(debit.outstanding_total)}</p>
          <ul className="mt-2 space-y-1.5 text-sm">
            {outstanding.map((c, i) => (
              <li key={i} className="flex flex-wrap justify-between gap-2">
                <span className="text-slate-700">{c.concept}</span>
                <span className={c.status === "fallido" ? "text-red-700 font-medium" : "text-slate-600"}>
                  {fmtMoney(c.amount)} · {c.status === "fallido" ? "rechazado" : c.sent ? "enviado a Armony" : "en tu próximo débito"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isPastDue && isDebit && (
        <div className="card-obsidian p-5 border-l-4 border-red-500" data-testid="past-due-banner">
          <p className="text-sm font-medium text-red-700">Armony no pudo cobrar tu plan</p>
          <p className="text-sm text-slate-600 mt-1">
            Por ahora sigues teniendo tu plan. Comunícate con Armony para regularizar tu débito: si el cobro no se confirma en {debit.grace_days} días desde que se envió, el plan termina.
          </p>
          <Button data-testid="cancel-plan-button" variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="mt-3 rounded-full border-slate-300 text-slate-700">
            Prefiero cancelar el plan
          </Button>
        </div>
      )}

      {isPastDue && !isDebit && (
        <div className="card-obsidian p-5 border-l-4 border-red-500" data-testid="past-due-banner">
          <p className="text-sm font-medium text-red-700">Tenemos un problema con el pago de tu plan</p>
          <p className="text-sm text-slate-600 mt-1">
            Stripe volverá a intentar el cobro. Si el problema continúa, contacta a Armony para actualizar tu método de pago.
          </p>
          <Button data-testid="cancel-plan-button" variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="mt-3 rounded-full border-slate-300 text-slate-700">
            Prefiero cancelar el plan
          </Button>
        </div>
      )}

      <div className="inline-flex items-center rounded-full bg-navy/5 p-1" data-testid="interval-toggle">
        <button
          data-testid="interval-month"
          onClick={() => setInterval("month")}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${interval === "month" ? "bg-navy text-white" : "text-slate-600 hover:text-navy"}`}
        >
          Mensual
        </button>
        <button
          data-testid="interval-year"
          onClick={() => setInterval("year")}
          className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${interval === "year" ? "bg-navy text-white" : "text-slate-600 hover:text-navy"}`}
        >
          Anual <span className="text-gold-ink font-semibold">· ahorra</span>
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {data.plans.map((plan: any) => {
          const isFree = !!plan.free;
          const price = isFree ? null : plan.prices[interval];
          const isCurrent = isFree
            ? !hasActive && !isPastDue
            : (hasActive || isPastDue) && current.plan === plan.id && current.plan_interval === interval;
          return (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`card-obsidian p-7 flex flex-col relative overflow-hidden ${plan.highlight ? "ring-2 ring-gold" : ""}`}
            >
              {plan.highlight && (
                <span className="self-start mb-3 inline-flex items-center gap-1 text-xs font-semibold text-gold-ink bg-gold/10 px-3 py-1 rounded-full">
                  <Crown className="w-3.5 h-3.5" /> Recomendado
                </span>
              )}
              <div className="flex items-center gap-2">
                {isFree ? <Star className="w-5 h-5 text-navy" /> : plan.highlight ? <Crown className="w-5 h-5 text-gold" /> : <Sparkles className="w-5 h-5 text-navy" />}
                <h2 className="text-xl font-serif font-medium">{plan.name}</h2>
              </div>
              <p className="text-sm text-slate-500 mt-1">{plan.tagline}</p>

              <div className="mt-5 flex items-baseline gap-1">
                {isFree ? (
                  <span className="text-4xl font-bold text-navy" data-testid={`plan-price-${plan.id}`}>Gratis</span>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-navy" data-testid={`plan-price-${plan.id}`}>
                      ${price.amount.toFixed(2)}
                    </span>
                    <span className="text-slate-500 text-sm">/{interval === "year" ? "año" : "mes"}</span>
                  </>
                )}
              </div>
              {isFree ? (
                <p className="text-xs text-slate-400 mt-1">Incluido con tu membresía Armony</p>
              ) : interval === "year" ? (
                <p className="text-xs text-gold-ink font-medium mt-1">Facturación anual en un solo pago</p>
              ) : (
                <p className="text-xs text-slate-400 mt-1">Facturación mensual</p>
              )}

              {plan.includes_note && (
                <p className="mt-5 text-xs font-semibold text-navy uppercase tracking-wide">{plan.includes_note}</p>
              )}
              <ul className={`${plan.includes_note ? "mt-3" : "mt-6"} space-y-3 flex-1`}>
                {plan.features.map((f: string, i: number) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {isFree ? (
                <div className="mt-7 w-full rounded-full font-semibold text-center py-2.5 text-sm bg-navy/5 text-navy" data-testid="plan-free-status">
                  {isCurrent ? "Tu plan actual" : "Plan base incluido"}
                </div>
              ) : (
                <Button
                  data-testid={`subscribe-${plan.id}`}
                  disabled={!!loadingKey || isCurrent || isPastDue || cancelScheduled || !!pendingRequest}
                  onClick={() =>
                    hasActive && isDebit
                      ? setCheckout({ lookupKey: price.lookup_key, label: `${plan.name} · ${interval === "year" ? "Anual" : "Mensual"}` })
                      : hasActive
                      ? changePlan(price.lookup_key, `${plan.name} · ${price.label}`, interval)
                      : navigate(`/portal/planes/contratar?plan=${plan.id}&modalidad=${interval}`)
                  }
                  className={`mt-7 w-full rounded-full font-semibold ${plan.highlight ? "gold-gradient-bg text-white" : "bg-navy text-white hover:bg-navy/90"}`}
                >
                  {loadingKey === price.lookup_key ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isCurrent ? (
                    isPastDue ? "Plan actual · pago pendiente" : "Plan actual"
                  ) : hasActive ? (
                    `Cambiar a este plan · ${price.label}`
                  ) : (
                    `Suscribirme · ${price.label}`
                  )}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-400">
        Al contratar eliges cómo pagar: con tarjeta (de forma segura con Stripe){debit?.eligible ? " o sumándolo a tu débito automático de Armony" : ""}. Tras el pago, en unos minutos podrás entrar a la app Armony Contigo con tu
        mismo correo y contraseña de Armony. Puedes cancelar tu plan cuando quieras desde esta página: sigue activo hasta el final del periodo pagado.
      </p>
      <CancelPlanDialog open={cancelOpen} onOpenChange={closeCancel} onDone={() => { load(); refresh(); }} />
      <PlanCheckoutDialog
        open={!!checkout}
        onOpenChange={(o) => !o && setCheckout(null)}
        lookupKey={checkout?.lookupKey || null}
        planLabel={checkout?.label || ""}
        onDone={() => { load(); refresh(); }}
      />
    </div>
  );
}
